import { WebLink } from '../../../../../app-kit/components/WebLink/WebLink';
import { MOBILE_MAX, Text, TimeLg } from '@property.com.au/ui-kit';
import {
  Asterisk,
  BYBFullPrice,
  BYBLogo,
  BYBPrice,
  BYBPriceAndLogoContainer,
  BYBPriceContainer,
  ETA,
  ETAContainer,
  EstimationCard,
  InfoSection,
  StyledStack,
  BYBPriceLoadingContainer,
  BYBPriceTitleSkeleton,
  BYBPriceDisclaimerSkeleton,
  BYBPriceTitle,
  BYBPriceDisclaimer,
  BYBEtaSkeletonText,
} from '../../BeforeYouBuyModal.styled';
import { ModalDialog } from '../../../../../components/ModalDialog/ModalDialog';
import type { BeforeYouBuyModalDialogProps } from '../../types';
import type { FunctionComponent } from 'react';
import { LoadingDots } from '../../../../../app-kit/components/LoadingDots/LoadingDots';
import { BeforeYouBuyThirdPartyModalDisclaimers } from './BeforeYouBuyThirdPartyModalDisclaimers';
import beforeYouBuyLogoSvg from '../../../../../assets/beforeyoubuy-logo.svg';
import { DynamicContent } from '../../../../../app-kit/components/DynamicContent/DynamicContent';
import { BeforeYouBuyModalFeatureList } from '../BeforeYouBuyModalFeatureList';
import { useFeatureRollout } from '@rea-argonaut/experiment';

const BeforeYouBuyEstimationLoadingCard: FunctionComponent = () => {
  return (
    <EstimationCard>
      <BYBPriceTitleSkeleton
        borderRadius="large"
        height="subtitle02"
        width="50%"
        dangerouslySetClassNames={{
          skeletonTextContainer: 'skeletonTextContainer',
        }}
      />
      <BYBPriceAndLogoContainer>
        <BYBPriceLoadingContainer>
          <BYBPrice>$</BYBPrice>
          <LoadingDots />
        </BYBPriceLoadingContainer>
        <BYBLogo loading="lazy" alt="Before You Buy" src={beforeYouBuyLogoSvg} />
      </BYBPriceAndLogoContainer>
      <BYBPriceDisclaimerSkeleton
        borderRadius="large"
        height="body02"
        gap="twoExtraSmall"
        numOfLines={2}
        dangerouslySetClassNames={{
          skeletonTextContainer: 'skeletonTextContainer',
        }}
      />
    </EstimationCard>
  );
};

export interface BeforeYouBuyModalContentProps extends BeforeYouBuyModalDialogProps {
  onClose: () => void | Promise<void>;
}

export const BeforeYouBuyModalContent: FunctionComponent<BeforeYouBuyModalContentProps> = ({
  title,
  propertyAddress,
  beforeYouBuyLink,
  featureTitle,
  featureList,
  featureListV2,
  eta,
  priceBlock,
  onClose,
  loading,
}) => {
  const isBYBLegalDisclaimersEnabled = useFeatureRollout('byb_legal_disclaimers').isEnabled();
  const priceTitle = isBYBLegalDisclaimersEnabled
    ? priceBlock?.priceTitleV2
    : priceBlock?.priceTitle;
  return (
    <ModalDialog
      autoFullScreen
      fullScreenMediaQuery={`(max-width: ${MOBILE_MAX}px)`}
      onClose={() => void onClose()}
      header={
        <StyledStack gap="0.125rem">
          <Text as="h1" variant="subtitle01">
            {title}
          </Text>
          <Text as="h2" variant="body01">
            {propertyAddress}
          </Text>
        </StyledStack>
      }
      footer={
        <WebLink
          key={beforeYouBuyLink.key}
          title={beforeYouBuyLink.title}
          url={beforeYouBuyLink.url}
          variant={beforeYouBuyLink.variant}
          isExternal={beforeYouBuyLink.isExternal}
          tracking={beforeYouBuyLink.tracking}
          iconInfo={beforeYouBuyLink.iconInfo}
          disabled={loading}
          fullWidth
          onClick={() => void onClose()}
          rel="noopener"
        />
      }
    >
      {loading ? (
        <BeforeYouBuyEstimationLoadingCard />
      ) : (
        <EstimationCard>
          {priceTitle && <BYBPriceTitle>{priceTitle}</BYBPriceTitle>}
          <BYBPriceAndLogoContainer>
            {priceBlock?.price && (
              <BYBPriceContainer>
                {priceBlock?.price && (
                  <BYBPrice>
                    {priceBlock.price}
                    <Asterisk>*</Asterisk>
                  </BYBPrice>
                )}
                {priceBlock?.fullCost && <BYBFullPrice>{priceBlock.fullCost}</BYBFullPrice>}
              </BYBPriceContainer>
            )}
            <BYBLogo loading="lazy" alt="Before You Buy" src={beforeYouBuyLogoSvg} />
          </BYBPriceAndLogoContainer>
          {priceBlock?.disclaimerV2 && priceBlock?.disclaimerV2.length > 0 && (
            <BYBPriceDisclaimer>
              <DynamicContent dynamicContents={priceBlock?.disclaimerV2 ?? []} />
            </BYBPriceDisclaimer>
          )}
        </EstimationCard>
      )}
      <InfoSection>
        <p>{featureTitle}</p>
        <BeforeYouBuyModalFeatureList featureList={featureList} featureListV2={featureListV2} />
      </InfoSection>

      <ETAContainer>
        <TimeLg />
        {loading ? (
          <BYBEtaSkeletonText
            dangerouslySetClassNames={{
              skeletonTextContainer: 'skeletonTextContainer',
            }}
            borderRadius="large"
          />
        ) : (
          <ETA>{eta}</ETA>
        )}
      </ETAContainer>
      <BeforeYouBuyThirdPartyModalDisclaimers />
    </ModalDialog>
  );
};
